import React, { useEffect, useState, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { useParams, useNavigate, useLocation } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '/api';

function Results() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [summary, setSummary] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [riskInfo, setRiskInfo] = useState({ riskScore: null, riskExplanation: '' });
  const [jobInfo, setJobInfo] = useState({ planType: '', paid: false });
  const [riskLoading, setRiskLoading] = useState(true);
  const [jobLoading, setJobLoading] = useState(true);
  const [jobStatus, setJobStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const intervalRef = useRef();
  const [purchaseCheckLoading, setPurchaseCheckLoading] = useState(true);
  const [paymentError, setPaymentError] = useState(null);
  const [hideUpgradeButton, setHideUpgradeButton] = useState(false);
  const [showStripeRedirect, setShowStripeRedirect] = useState(false);
  const [pollInterval, setPollInterval] = useState(5000); // ms
  const pollingStartTimeRef = useRef(null);
  const [pagesCounted, setPagesCounted] = useState(false);
  const [etcData, setEtcData] = useState(null);
  const [etcLoading, setEtcLoading] = useState(true);
  const [etcDateTime, setEtcDateTime] = useState(null);

  // Read upgrade param from query string
  const searchParams = new URLSearchParams(location.search);
  const upgradeStatus = searchParams.get('upgrade');

  // Handle payment redirect status
  useEffect(() => {
    if (upgradeStatus === 'cancel') {
      setPaymentError('Payment was cancelled or failed. Please try again.');
    }
  }, [upgradeStatus]);

  // Hide upgrade button if payment is taking too long
  useEffect(() => {
    if (paymentError === 'Payment was successful, but the upgrade is taking longer than expected. Please refresh or try again later.') {
      setHideUpgradeButton(true);
    } else {
      setHideUpgradeButton(false);
    }
  }, [paymentError]);

  // Poll purchase check if upgrade=success
  useEffect(() => {
    let cancelled = false;
    let pollInterval = null;
    let timeout = null;
    async function pollPurchaseCheck() {
      try {
        const res = await fetch(`${API_BASE}/purchase-check?jobId=${encodeURIComponent(jobId)}`);
        if (res.ok) {
          const data = await res.json();
          if (data.upgradedJobId) {
            if (jobInfo && jobInfo.planType === 'free') {
              setPurchaseCheckLoading(false);
              navigate(`/results/${data.upgradedJobId}`, { replace: true });
              return;
            } else if (jobInfo && jobInfo.planType !== 'free') {
              if (data.upgradedJobId !== jobId) {
                setPurchaseCheckLoading(false);
                navigate(`/results/${data.upgradedJobId}`, { replace: true });
                return;
              } else {
                // upgradedJobId matches current job, do nothing
                setPurchaseCheckLoading(false);
                cancelled = true;
                if (pollInterval) {
                  clearInterval(pollInterval);
                }
                return;
              }
            }
          }
        }
      } catch {}
      // Do not set purchaseCheckLoading to false here; only when polling ends
    }
    if (upgradeStatus === 'success') {
      setPurchaseCheckLoading(true);
      // Poll every 3 seconds, timeout after 30 seconds
      pollPurchaseCheck();
      pollInterval = setInterval(() => {
        if (!cancelled) {
          pollPurchaseCheck();
        } else {
          clearInterval(pollInterval);
        }
      }, 5000);
      timeout = setTimeout(() => {
        clearInterval(pollInterval);
        setPurchaseCheckLoading(false);
        if (!cancelled) {        
          setPaymentError('Payment was successful, but the upgrade is taking longer than expected. Please refresh or try again later.');
        }
      }, 60000);
      return () => {
        cancelled = true;
        clearInterval(pollInterval);
        clearTimeout(timeout);
        setPurchaseCheckLoading(false);
      };
    } else {
      setPurchaseCheckLoading(true);
      pollPurchaseCheck().finally(() => {
        if (!cancelled) setPurchaseCheckLoading(false);
      });
      return () => { cancelled = true; setPurchaseCheckLoading(false); };
    }
  }, [jobId, navigate, upgradeStatus, jobInfo]);

  

  const createPaymentLinkAndRoute = async () => {
    try {
      const res = await fetch(`${API_BASE}/create-payment-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ jobId }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.url) {
          setShowStripeRedirect(true);
          setTimeout(() => {
            window.location.href = data.url;
          }, 2000);
        }
      }
    } catch {}
  };
  
  // Refactor: Always fetch job JSON on mount
  useEffect(() => {
    let cancelled = false;
    async function fetchJobJson() {
      setJobLoading(true);
      setError(null);
      try {
        const res = await fetch(`${API_BASE}/download?jobId=${encodeURIComponent(jobId)}&type=job`);
        if (!res.ok) {
          setError('Job not found. Please check your link or try again.');
          setJobLoading(false);
          return;
        }
        const job = await res.json();
        if (!cancelled) {
          setJobInfo(job);
          setJobLoading(false);
        }
      } catch (err) {
        setError('Could not fetch job info.');
        setJobLoading(false);
      }
    }
    if (jobId) fetchJobJson();
    return () => { cancelled = true; };
  }, [jobId]);

  // 1. Initial polling for 'pages_counted' (only for quick pay and not paid)
  useEffect(() => {
    if (!jobInfo || !jobInfo.planType) return;
    if (jobInfo.planType === 'free' || jobInfo.paid) {
      setPagesCounted(true); // Skip polling, proceed directly
      return;
    }
    // Only poll if quick pay and not paid
    if (!jobId) return;
    let cancelled = false;
    let intervalId = null;

    async function pollForPagesCounted() {
      setStatusLoading(true);
      try {
        const statusResponse = await fetch(`${API_BASE}/status?jobId=${encodeURIComponent(jobId)}`);
        let status = null;
        if (statusResponse.ok) {
          status = await statusResponse.json();
          if (!cancelled) setJobStatus(status);
        }
        if (!statusResponse.ok || cancelled) return;
        setStatusLoading(false);
        if (status.status === 'pages_counted') {
          setPagesCounted(true);
          clearInterval(intervalId);
        }
      } catch (err) {
        setStatusLoading(false);
        setJobStatus({ status: 'error', error: err.message });
      }
    }

    pollForPagesCounted();
    intervalId = setInterval(pollForPagesCounted, 5000); // Always 5 seconds

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [jobId, jobInfo]);

  // 2. After 'pages_counted', proceed to payment, purchase check, and main polling
  useEffect(() => {
    if (!pagesCounted) return;
    // Fetch job JSON if not already done
    if (!jobInfo || !jobInfo.planType) return;
    // Quick pay check and Stripe modal logic
    if (jobInfo.planType !== 'free' && !jobInfo.paid) {
      createPaymentLinkAndRoute();
      return;
    }
    // Purchase check and hard redirect if upgraded
    let cancelled = false;
    async function doPurchaseCheck() {
      setPurchaseCheckLoading(true);
      try {
        const res = await fetch(`${API_BASE}/purchase-check?jobId=${encodeURIComponent(jobId)}`);
        if (res.ok) {
          const data = await res.json();
          if (data.upgradedJobId && jobId !== data.upgradedJobId) {
            window.location.replace(`/results/${data.upgradedJobId}`);
            return;
          }
        }
      } catch {}
      if (!cancelled) setPurchaseCheckLoading(false);
    }
    doPurchaseCheck();
    return () => { cancelled = true; };
  }, [pagesCounted, jobId, jobInfo]);

  // 3. Main polling for results after all checks
  useEffect(() => {
    if (!pagesCounted) return;
    if (!jobInfo || !jobInfo.planType) return;
    if (jobInfo.planType !== 'free' && !jobInfo.paid) return; // Wait for payment
    if (purchaseCheckLoading) return; // Wait for purchase check
    if (!jobId) return;
    let cancelled = false;
    let intervalId = null;
    pollingStartTimeRef.current = Date.now();

    async function fetchStatusAndMaybeResults() {
      setStatusLoading(true);
      try {
        const statusResponse = await fetch(`${API_BASE}/status?jobId=${encodeURIComponent(jobId)}`);
        let status = null;
        if (statusResponse.ok) {
          status = await statusResponse.json();
          if (!cancelled) setJobStatus(status);
        }
        if (!statusResponse.ok || cancelled) return;
        setStatusLoading(false);
        // If job is complete, fetch results
        if (status.status === 'risk_score_generated' || status.status === 'complete') {
          setLoading(true);
          setError(null);
          fetch(`${API_BASE}/download?jobId=${encodeURIComponent(jobId)}&type=preview`)
            .then(res => {
              if (!res.ok) throw new Error('Failed to fetch summary');
              return res.text();
            })
            .then(text => {
              setSummary(text);
              setLoading(false);
            })
            .catch(err => {
              setError('Could not load summary.');
              setLoading(false);
              console.error(err);
            });
          fetch(`${API_BASE}/download?jobId=${encodeURIComponent(jobId)}&type=risk`)
            .then(res => {
              if (!res.ok) throw new Error('Failed to fetch risk');
              return res.json();
            })
            .then(resJson => {
              setRiskInfo(resJson);
            })
            .catch(err => {
              setError('Could not load risk information');
              console.error(err);
            })
            .finally(() => {
              setRiskLoading(false);
            });
          clearInterval(intervalId);
        }
      } catch (err) {
        setStatusLoading(false);
        setJobStatus({ status: 'error', error: err.message });
      }
    }

    fetchStatusAndMaybeResults();
    intervalId = setInterval(() => {
      // Dynamic polling interval
      const elapsed = Date.now() - pollingStartTimeRef.current;
      if (elapsed > 20 * 60 * 1000 && pollInterval !== 30000) {
        setPollInterval(30000);
        clearInterval(intervalId);
        intervalId = setInterval(fetchStatusAndMaybeResults, 30000);
      } else {
        fetchStatusAndMaybeResults();
      }
    }, pollInterval);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [pagesCounted, jobId, jobInfo, purchaseCheckLoading, pollInterval]);

  // After the main polling effect
  useEffect(() => {
    if (!jobId) return;
    if (jobStatus && (jobStatus.status === 'risk_score_generated' || jobStatus.status === 'complete')) return;
    if (etcData) return;
    let cancelled = false;
    let intervalId = null;

    async function pollEtc() {
      try {
        const etcRes = await fetch(`${API_BASE}/download?jobId=${encodeURIComponent(jobId)}&type=etc`);
        if (etcRes.ok) {
          const etcJson = await etcRes.json();
          if (!cancelled) {
            setEtcData(etcJson);
            setEtcLoading(false);
            // Calculate ETC date/time
            if (etcJson.estimatedCompletionSeconds) {
              const now = new Date();
              const etcDate = new Date(now.getTime() + etcJson.estimatedCompletionSeconds * 1000);
              setEtcDateTime(etcDate);
            }
          }
          clearInterval(intervalId);
        }
      } catch (err) {
        // Ignore, keep polling
      }
    }

    pollEtc();
    intervalId = setInterval(pollEtc, 5000);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [jobId, jobStatus, etcData]);

  if (!jobId) return null;

  const statusMap = {
    uploaded: 'Waiting to start processing...',
    breaking_pdf_into_images: 'Splitting PDF into images...',
    pdf_images_created: 'PDF images created. Preparing for OCR...',
    ocring_page: s => `OCR in progress...`,
    ocr_page_complete: s => `OCR in progress...`,
    summarizing_chunk: s => `Summarizing...`,
    chunk_summarized: s => `Summarizing...`,
    risk_scored_chunk: s => 'Generating risk score...',
    generating_risk_score: 'Generating risk score...',
    generating_preview_text: 'Preparing preview...',
    risk_score_generated: 'Analysis complete!',
    complete: 'Analysis complete!',
    extracting_topics: 'Extracting Topics...',
    pages_counted: 'Tallied up pages...',
    error: s => `Error: ${s.error || 'Unknown error'}`
  };  

  if (purchaseCheckLoading) {
    return (
      <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-md p-8 text-center">
        <div className="flex items-center justify-center mb-2">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3"></div>
          <div className="text-lg font-medium text-blue-800">Checking for upgrade...</div>
        </div>
      </div>
    );
  }

  const renderStatus = () => {
    if (!jobStatus) return <span>Checking job status...</span>;
    const s = jobStatus;
    const msg = typeof statusMap[s.status] === 'function' ? statusMap[s.status](s) : statusMap[s.status] || s.status;
    return <span>{msg}</span>;
  };

  const isComplete = jobStatus && (jobStatus.status === 'risk_score_generated' || jobStatus.status === 'complete');

  return (
    <>
      {showStripeRedirect && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center max-w-sm">
            <div className="mb-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
              <div className="text-lg font-semibold text-blue-800">Redirecting to Stripe for secure payment...</div>
            </div>
            <div className="text-gray-500 text-sm">Please wait, you will be redirected shortly.</div>
          </div>
        </div>
      )}
      <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-md p-8 text-center">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">Legal Document Analysis Results</h2>
        {paymentError && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">{paymentError}</div>
        )}
        {jobInfo.planType && (
          <div className="mb-4">
            <span className="inline-block px-3 py-1 rounded-full text-sm font-semibold "
              style={{ background: jobInfo.planType === 'free' ? '#f3f4f6' : '#dbeafe', color: jobInfo.planType === 'free' ? '#374151' : '#1d4ed8' }}>
              {jobInfo.planType === 'free' ? 'Free' : 'Quick Check'}
              {jobInfo.processedPageCount !== null && jobInfo.fullPageCount !== null && jobInfo.processedPageCount < jobInfo.fullPageCount && (
                <span> (first 3 pages)</span>
              )}
            </span>
          </div>
        )}
        {!isComplete && (
          <div className="mb-8">
            <div className="flex items-center justify-center mb-2">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3"></div>
              <div className="text-lg font-medium text-blue-800">{renderStatus()}</div>
            </div>
            <div className="text-gray-500 text-sm">This may take a few minutes for large documents.</div>
          </div>
        )}
        {isComplete && (
          <>
            <div className="mb-6">
              <div className="text-xl font-semibold text-blue-800 mb-2">
                Risk Score: <span className="text-2xl">{riskInfo.riskScore ?? '-'}</span> / 5
              </div>
              <div className="text-gray-700 text-base mb-2 min-h-[24px]">
                {riskLoading ? 'Loading risk explanation...' : riskInfo.riskExplanation || ''}
              </div>
            </div>
            <div className="mb-8 text-left">
              <h3 className="text-lg font-semibold text-gray-900 mb-2 text-center">Summary Preview</h3>
              <div className="prose prose-blue max-w-none bg-gray-50 border border-gray-200 rounded-lg p-4 min-h-[120px]">
                {loading ? (
                  <div className="text-gray-500">Loading summary...</div>
                ) : error ? (
                  <div className="text-red-600">{error}</div>
                ) : (
                  <ReactMarkdown>{summary}</ReactMarkdown>
                )}
              </div>
            </div>
            {/* Improved button layout */}
            <div className="flex flex-col items-center gap-6 mt-6">
              {/* Primary Action */}
              {jobInfo.planType === 'free' && !hideUpgradeButton && (
                <button
                  onClick={createPaymentLinkAndRoute}
                  className="w-full px-6 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors"
                >
                  Upgrade to Quick Check
                </button>
              )}
              {/* Divider */}
              <div className="w-full border-t border-gray-200 my-2"></div>
              {/* Secondary Actions */}
              <div className="flex flex-col sm:flex-row gap-4 w-full">
                <a
                  href={`${API_BASE}/generate-pdf?jobId=${encodeURIComponent(jobId)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors text-center"
                >
                  {jobInfo.planType === 'free' ? 'Download PDF of Preview' : 'Download PDF of Full Quick Check'}
                </a>
                <a
                  href={`${API_BASE}/download?jobId=${encodeURIComponent(jobId)}&type=source`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 px-6 py-3 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 transition-colors text-center"
                >
                  Download Original Document
                </a>
              </div>
              <button
                onClick={() => navigate('/')}
                className="w-full mt-4 px-6 py-3 bg-gray-200 text-gray-800 rounded-lg font-medium hover:bg-gray-300 transition-colors"
              >
                Upload Another Document
              </button>
            </div>
          </>
        )}
        {etcData && !isComplete && (
          <div className="mb-2 text-gray-600 text-sm">
            Estimated time to complete: {Math.ceil(etcData.estimatedCompletionSeconds / 60)} min<br/>
            Estimated completion: {etcDateTime ? etcDateTime.toLocaleString() : ''}
          </div>
        )}
      </div>
    </>
  );
}

// ResultsDemo: stripped-down version for demo/example use
export function ResultsDemo() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [summary, setSummary] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [riskInfo, setRiskInfo] = useState({ riskScore: null, riskExplanation: '' });
  const [jobInfo, setJobInfo] = useState({ planType: '', paid: false });
  const [riskLoading, setRiskLoading] = useState(true);
  const [jobLoading, setJobLoading] = useState(true);
  const [jobStatus, setJobStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [pagesCounted, setPagesCounted] = useState(false);
  const [etcData, setEtcData] = useState(null);
  const [etcLoading, setEtcLoading] = useState(true);
  const [etcDateTime, setEtcDateTime] = useState(null);

  // Only fetch job info and status, no upgrade logic
  useEffect(() => {
    let cancelled = false;
    async function fetchJobJson() {
      setJobLoading(true);
      setError(null);
      try {
        const res = await fetch(`${API_BASE}/download?jobId=${encodeURIComponent(jobId)}&type=job`);
        if (!res.ok) {
          setError('Job not found. Please check your link or try again.');
          setJobLoading(false);
          return;
        }
        const job = await res.json();
        if (!cancelled) {
          setJobInfo(job);
          setJobLoading(false);
        }
      } catch (err) {
        setError('Could not fetch job info.');
        setJobLoading(false);
      }
    }
    if (jobId) fetchJobJson();
    return () => { cancelled = true; };
  }, [jobId]);

  // Poll for status and results (no upgrade logic)
  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    let intervalId = null;
    async function fetchStatusAndMaybeResults() {
      setStatusLoading(true);
      try {
        const statusResponse = await fetch(`${API_BASE}/status?jobId=${encodeURIComponent(jobId)}`);
        let status = null;
        if (statusResponse.ok) {
          status = await statusResponse.json();
          if (!cancelled) setJobStatus(status);
        }
        if (!statusResponse.ok || cancelled) return;
        setStatusLoading(false);
        // If job is complete, fetch results
        if (status.status === 'risk_score_generated' || status.status === 'complete') {
          setLoading(true);
          setError(null);
          fetch(`${API_BASE}/download?jobId=${encodeURIComponent(jobId)}&type=preview`)
            .then(res => {
              if (!res.ok) throw new Error('Failed to fetch summary');
              return res.text();
            })
            .then(text => {
              setSummary(text);
              setLoading(false);
            })
            .catch(err => {
              setError('Could not load summary.');
              setLoading(false);
              console.error(err);
            });
          fetch(`${API_BASE}/download?jobId=${encodeURIComponent(jobId)}&type=risk`)
            .then(res => {
              if (!res.ok) throw new Error('Failed to fetch risk');
              return res.json();
            })
            .then(resJson => {
              setRiskInfo(resJson);
            })
            .catch(err => {
              setError('Could not load risk information');
              console.error(err);
            })
            .finally(() => {
              setRiskLoading(false);
            });
          clearInterval(intervalId);
        }
      } catch (err) {
        setStatusLoading(false);
        setJobStatus({ status: 'error', error: err.message });
      }
    }
    fetchStatusAndMaybeResults();
    intervalId = setInterval(fetchStatusAndMaybeResults, 5000);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [jobId]);

  if (!jobId) return null;

  const statusMap = {
    uploaded: 'Waiting to start processing...',
    breaking_pdf_into_images: 'Splitting PDF into images...',
    pdf_images_created: 'PDF images created. Preparing for OCR...',
    ocring_page: s => `OCR in progress...`,
    ocr_page_complete: s => `OCR in progress...`,
    summarizing_chunk: s => `Summarizing...`,
    chunk_summarized: s => `Summarizing...`,
    risk_scored_chunk: s => 'Generating risk score...',
    generating_risk_score: 'Generating risk score...',
    generating_preview_text: 'Preparing preview...',
    risk_score_generated: 'Analysis complete!',
    complete: 'Analysis complete!',
    extracting_topics: 'Extracting Topics...',
    pages_counted: 'Tallied up pages...',
    error: s => `Error: ${s.error || 'Unknown error'}`
  };

  const isComplete = jobStatus && (jobStatus.status === 'risk_score_generated' || jobStatus.status === 'complete');

  return (
    <>
      <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-md p-8 text-center">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">Legal Document Analysis Results</h2>
        {jobInfo.planType && (
          <div className="mb-4">
            <span className="inline-block px-3 py-1 rounded-full text-sm font-semibold "
              style={{ background: jobInfo.planType === 'free' ? '#f3f4f6' : '#dbeafe', color: jobInfo.planType === 'free' ? '#374151' : '#1d4ed8' }}>
              {jobInfo.planType === 'free' ? 'Free' : 'Quick Check'}
              {jobInfo.processedPageCount !== null && jobInfo.fullPageCount !== null && jobInfo.processedPageCount < jobInfo.fullPageCount && (
                <span> (first 3 pages)</span>
              )}
            </span>
          </div>
        )}
        {!isComplete && (
          <div className="mb-8">
            <div className="flex items-center justify-center mb-2">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3"></div>
              <div className="text-lg font-medium text-blue-800">{statusMap[jobStatus?.status] || 'Checking job status...'}</div>
            </div>
            <div className="text-gray-500 text-sm">This may take a few minutes for large documents.</div>
          </div>
        )}
        {isComplete && (
          <>
            <div className="mb-6">
              <div className="text-xl font-semibold text-blue-800 mb-2">
                Risk Score: <span className="text-2xl">{riskInfo.riskScore ?? '-'}</span> / 5
              </div>
              <div className="text-gray-700 text-base mb-2 min-h-[24px]">
                {riskLoading ? 'Loading risk explanation...' : riskInfo.riskExplanation || ''}
              </div>
            </div>
            <div className="mb-8 text-left">
              <h3 className="text-lg font-semibold text-gray-900 mb-2 text-center">Summary Preview</h3>
              <div className="prose prose-blue max-w-none bg-gray-50 border border-gray-200 rounded-lg p-4 min-h-[120px]">
                {loading ? (
                  <div className="text-gray-500">Loading summary...</div>
                ) : error ? (
                  <div className="text-red-600">{error}</div>
                ) : (
                  <ReactMarkdown>{summary}</ReactMarkdown>
                )}
              </div>
            </div>
            {/* Improved button layout, no upgrade */}
            <div className="flex flex-col items-center gap-6 mt-6">
              {/* Divider */}
              <div className="w-full border-t border-gray-200 my-2"></div>
              {/* Secondary Actions */}
              <div className="flex flex-col sm:flex-row gap-4 w-full">
                <a
                  href={`${API_BASE}/generate-pdf?jobId=${encodeURIComponent(jobId)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors text-center"
                >
                  {jobInfo.planType === 'free' ? 'Download PDF of Preview' : 'Download PDF of Full Quick Check'}
                </a>
                <a
                  href={`${API_BASE}/download?jobId=${encodeURIComponent(jobId)}&type=source`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 px-6 py-3 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 transition-colors text-center"
                >
                  Download Original Document
                </a>
              </div>
              <button
                onClick={() => navigate('/')}
                className="w-full mt-4 px-6 py-3 bg-gray-200 text-gray-800 rounded-lg font-medium hover:bg-gray-300 transition-colors"
              >
                Upload Your Document
              </button>
            </div>
          </>
        )}
        {etcData && !isComplete && (
          <div className="mb-2 text-gray-600 text-sm">
            Estimated time to complete: {Math.ceil(etcData.estimatedCompletionSeconds / 60)} min<br/>
            Estimated completion: {etcDateTime ? etcDateTime.toLocaleString() : ''}
          </div>
        )}
      </div>
    </>
  );
}

export default Results; 